import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  MenuItem,
  Typography,
  Divider,
  Box,
  Alert,
} from '@mui/material';
import { Account, AccountCreate } from '../types/account';
import AddressAutocomplete from './AddressAutocomplete';

interface AccountFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (account: AccountCreate) => Promise<void>;
  account?: Account | null;
}

const AccountFormDialog: React.FC<AccountFormDialogProps> = ({
  open,
  onClose,
  onSave,
  account,
}) => {
  const isEdit = !!account;
  
  const [formData, setFormData] = useState<AccountCreate>({
    sonic_account_number: account?.sonic_account_number || '',
    status: account?.status || 'active',
    account_type: account?.account_type || 'consumer',
    organisation_name: account?.organisation_name || '',
    contact_first_name: account?.contact_first_name || '',
    contact_last_name: account?.contact_last_name || '',
    mobile_number: account?.mobile_number || '',
    email: account?.email || '',
    main_phone: account?.main_phone || '',
    fax: account?.fax || '',
    website: account?.website || '',
    address_line1: account?.address_line1 || '',
    address_line2: account?.address_line2 || '',
    address_suburb: account?.address_suburb || '',
    address_state: account?.address_state || '',
    address_postcode: account?.address_postcode || '',
    address_country: account?.address_country || 'Australia',
    address_formatted: account?.address_formatted || '',
    abn: account?.abn || '',
    date_of_birth: account?.date_of_birth || '',
    notification_preferences: account?.notification_preferences || {
      email: true,
      sms: false,
    },
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Update form data when account prop changes or dialog opens
  useEffect(() => {
    if (open) {
      if (account) {
        setFormData({
          sonic_account_number: account.sonic_account_number || '',
          status: account.status || 'active',
          account_type: account.account_type || 'consumer',
          organisation_name: account.organisation_name || '',
          contact_first_name: account.contact_first_name || '',
          contact_last_name: account.contact_last_name || '',
          mobile_number: account.mobile_number || '',
          email: account.email || '',
          main_phone: account.main_phone || '',
          fax: account.fax || '',
          website: account.website || '',
          address_line1: account.address_line1 || '',
          address_line2: account.address_line2 || '',
          address_suburb: account.address_suburb || '',
          address_state: account.address_state || '',
          address_postcode: account.address_postcode || '',
          address_country: account.address_country || 'Australia',
          address_formatted: account.address_formatted || '',
          abn: account.abn || '',
          date_of_birth: account.date_of_birth || '',
          notification_preferences: account.notification_preferences || {
            email: true,
            sms: false,
          },
        });
      } else {
        setFormData({
          sonic_account_number: '',
          status: 'active',
          account_type: 'consumer',
          organisation_name: '',
          contact_first_name: '',
          contact_last_name: '',
          mobile_number: '',
          email: '',
          main_phone: '',
          fax: '',
          website: '',
          address_line1: '',
          address_line2: '',
          address_suburb: '',
          address_state: '',
          address_postcode: '',
          address_country: 'Australia',
          address_formatted: '',
          abn: '',
          date_of_birth: '',
          notification_preferences: {
            email: true,
            sms: false,
          },
        });
      }
      setError(null);
    }
  }, [open, account]);

  const handleChange = (field: keyof AccountCreate) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleNotificationChange = (field: 'email' | 'sms') => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData({
      ...formData,
      notification_preferences: {
        ...formData.notification_preferences!,
        [field]: e.target.checked,
      },
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      // Remove empty optional fields
      const cleanedData: any = { ...formData };
      if (!cleanedData.sonic_account_number) delete cleanedData.sonic_account_number;
      if (!cleanedData.organisation_name) delete cleanedData.organisation_name;
      if (!cleanedData.contact_first_name) delete cleanedData.contact_first_name;
      if (!cleanedData.contact_last_name) delete cleanedData.contact_last_name;
      if (!cleanedData.mobile_number) delete cleanedData.mobile_number;
      if (!cleanedData.email) delete cleanedData.email;
      if (!cleanedData.main_phone) delete cleanedData.main_phone;
      if (!cleanedData.fax) delete cleanedData.fax;
      if (!cleanedData.website) delete cleanedData.website;
      if (!cleanedData.abn) delete cleanedData.abn;
      if (cleanedData.account_type === 'consumer') delete cleanedData.abn;
      if (!cleanedData.date_of_birth) delete cleanedData.date_of_birth;

      await onSave(cleanedData);
      handleClose();
    } catch (err: any) {
      const message =
        err.response?.data?.detail ||
        (Array.isArray(err.response?.data?.detail) ? err.response.data.detail.join(' ') : null) ||
        err.message ||
        'Failed to save account';
      setError(typeof message === 'string' ? message : JSON.stringify(message));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        sonic_account_number: '',
        status: 'active',
        account_type: 'consumer',
        organisation_name: '',
        contact_first_name: '',
        contact_last_name: '',
        mobile_number: '',
        email: '',
        main_phone: '',
        fax: '',
        website: '',
        address_line1: '',
        address_line2: '',
        address_suburb: '',
        address_state: '',
        address_postcode: '',
        address_country: 'Australia',
        address_formatted: '',
        abn: '',
        date_of_birth: '',
        notification_preferences: { email: true, sms: false },
      });
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Account' : 'Add New Account'}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Account Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Sonic Account Number"
                value={formData.sonic_account_number}
                onChange={handleChange('sonic_account_number')}
                placeholder="SA123456"
                helperText="Optional - unique account identifier"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Status"
                value={formData.status}
                onChange={handleChange('status')}
                required
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Account Type"
                value={formData.account_type || 'consumer'}
                onChange={handleChange('account_type')}
              >
                <MenuItem value="consumer">Consumer</MenuItem>
                <MenuItem value="business">Business</MenuItem>
              </TextField>
            </Grid>
            {(formData.account_type === 'business') && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="ABN"
                  value={formData.abn || ''}
                  onChange={handleChange('abn')}
                  placeholder="11 digits"
                  helperText="Australian Business Number (11 digits)"
                />
              </Grid>
            )}
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle2" gutterBottom>
            Address
          </Typography>
          <Box sx={{ mb: 2 }}>
            <AddressAutocomplete
              value={{
                address_line1: formData.address_line1,
                address_line2: formData.address_line2,
                address_suburb: formData.address_suburb,
                address_state: formData.address_state,
                address_postcode: formData.address_postcode,
                address_country: formData.address_country,
                address_formatted: formData.address_formatted,
              }}
              onChange={(addr) => setFormData({ ...formData, ...addr })}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle2" gutterBottom>
            Contact Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Organisation Name"
                value={formData.organisation_name}
                onChange={handleChange('organisation_name')}
                placeholder="Acme Corporation Pty Ltd"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contact First Name"
                value={formData.contact_first_name}
                onChange={handleChange('contact_first_name')}
                placeholder="John"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contact Last Name"
                value={formData.contact_last_name}
                onChange={handleChange('contact_last_name')}
                placeholder="Smith"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Main Phone"
                value={formData.main_phone}
                onChange={handleChange('main_phone')}
                placeholder="02 1234 5678"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Fax"
                value={formData.fax}
                onChange={handleChange('fax')}
                placeholder="02 1234 5679"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleChange('email')}
                placeholder="john.smith@example.com"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Website"
                value={formData.website}
                onChange={handleChange('website')}
                placeholder="https://example.com"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Mobile Number"
                value={formData.mobile_number}
                onChange={handleChange('mobile_number')}
                placeholder="+61412345678"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Date of Birth"
                type="date"
                value={formData.date_of_birth || ''}
                onChange={handleChange('date_of_birth')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle2" gutterBottom>
            Notification Preferences
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.notification_preferences?.email || false}
                    onChange={handleNotificationChange('email')}
                  />
                }
                label="Email Notifications"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.notification_preferences?.sms || false}
                    onChange={handleNotificationChange('sms')}
                  />
                }
                label="SMS Notifications"
              />
            </Grid>
          </Grid>

          {!isEdit && (
            <Alert severity="info" sx={{ mt: 3 }}>
              After creating the account, you can add services to it from the accounts list.
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Saving...' : isEdit ? 'Update' : 'Create Account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccountFormDialog;
